import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faTimes, faCheck, faLayerGroup, faKeyboard, faListCheck, faSync } from '@fortawesome/free-solid-svg-icons';
import Modal from './ModalConversion';
import LoadingIcon from './LoadingIcon';
import { urlDistribution, endpointGetStock } from '../services/Endpoints';

const AddNotificationModal = ({ isOpen, onClose, onAdd, availableChannels }) => {
    const [selectedChannels, setSelectedChannels] = useState([]);
    const [activeTab, setActiveTab] = useState('manual');
    const [manualInkIds, setManualInkIds] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [projects, setProjects] = useState([]);
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [selectedSearchIds, setSelectedSearchIds] = useState([]);
    const [notificationType, setNotificationType] = useState('3');
    const [done, setDone] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setSubmitting(false);
        if (isOpen) {
            // Inicializamos con los alias de los canales disponibles
            setSelectedChannels(availableChannels.map(c => c.alias || c.channel.toLowerCase()));
            // reset search on open
            setSearchQuery('');
            setProjects([]);
            setManualInkIds('');
            setSelectedSearchIds([]);
            setNotificationType('3');
            setDone(0);
        }
    }, [isOpen, availableChannels]);

    // Debounce search ONLY if there is a query
    useEffect(() => {
        if (!isOpen || activeTab !== 'search' || !searchQuery) {
            if (!searchQuery) setProjects([]);
            return;
        }
        
        const timer = setTimeout(() => {
            fetchProjects(searchQuery);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery, activeTab, isOpen]);

    const fetchProjects = async (query = '') => {
        try {
            setLoadingProjects(true);
            const filterParam = query ? `&filter=${query}` : '';
            const response = await fetch(`${urlDistribution}${endpointGetStock}?page=1&limit=50${filterParam}`);
            const res = await response.json();
            if (res && res.status === 'success') {
                setProjects(res.data.projects || []);
            }
            setLoadingProjects(false);
        } catch (error) {
            console.error("Failed to fetch projects:", error);
            setLoadingProjects(false);
        }
    };

    const displayedProjects = useMemo(() => {
        return projects.slice(0, 50);
    }, [projects]);

    const handleChannelToggle = (channelObj) => {
        const alias = channelObj.alias || channelObj.channel.toLowerCase();
        setSelectedChannels(prev => 
            prev.includes(alias) ? prev.filter(c => c !== alias) : [...prev, alias]
        );
    };

    const handleProjectToggle = (fullId) => {
        setSelectedSearchIds(prev => 
            prev.includes(fullId) ? prev.filter(id => id !== fullId) : [...prev, fullId]
        );
    };

    const handleSubmit = () => {
        const inkIds = activeTab === 'manual' 
            ? manualInkIds.split(/[\s,]+/).filter(id => id.trim() !== '')
            : selectedSearchIds;

        if (selectedChannels.length === 0) {
            alert("Selecciona al menos un canal");
            return;
        }
        if (inkIds.length === 0) {
            alert("Agrega al menos un Ink ID");
            return;
        }

        setSubmitting(true);
        try {
            onAdd({ 
                channels: selectedChannels.join(','), 
                inkIds: inkIds.join(','),
                notificationType,
                done
            });
            // Decouple closure to ensure it doesn't block UI
            Promise.resolve().then(() => {
                onClose();
                setSubmitting(false);
            });
        } catch (error) {
            console.error("Submit failed:", error);
            setSubmitting(false);
        }
    };

    const inkIdsCount = activeTab === 'manual' 
        ? manualInkIds.split(/[\s,]+/).filter(id => id.trim() !== '').length
        : selectedSearchIds.length;
    const isInvalid = selectedChannels.length === 0 || inkIdsCount === 0;

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className="p-2 text-white">
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom border-secondary border-opacity-25 pb-3">
                    <div className="d-flex align-items-center gap-3">
                        <div className="bg-primary bg-opacity-10 p-2 rounded-3">
                            <FontAwesomeIcon icon={faPlus} className="text-primary" />
                        </div>
                        <div>
                            <h3 className="mb-0 text-white">Agregar Notificación</h3>
                            <p className="text-white small mb-0">Crea alertas para múltiples canales e Ink IDs</p>
                        </div>
                    </div>
                </div>

                <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="fw-bold mb-0 d-flex align-items-center gap-2 text-white opacity-75 small text-uppercase">
                            <FontAwesomeIcon icon={faLayerGroup} />
                            1. Seleccionar Canales
                        </label>
                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-primary p-0"
                                onClick={() => setSelectedChannels(availableChannels.map(c => c.alias || c.channel.toLowerCase()))}
                            >
                                Seleccionar todos
                            </button>
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-white-50 p-0"
                                onClick={() => setSelectedChannels([])}
                            >
                                Deseleccionar todos
                            </button>
                        </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {availableChannels.map(channel => {
                            const alias = channel.alias || channel.channel.toLowerCase();
                            const isSelected = selectedChannels.includes(alias);
                            return (
                                <button
                                    key={alias}
                                    type="button"
                                    className={`btn btn-sm rounded-pill px-3 transition-all ${
                                        isSelected 
                                            ? 'btn-primary shadow-sm' 
                                            : 'btn-outline-secondary opacity-50'
                                    }`}
                                    onClick={() => handleChannelToggle(channel)}
                                >
                                    <FontAwesomeIcon icon={isSelected ? faCheck : faPlus} className="me-2" />
                                    {channel.channel}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="mb-4">
                    <label className="fw-bold mb-3 d-flex align-items-center gap-2 text-white opacity-75 small text-uppercase">
                        <FontAwesomeIcon icon={faKeyboard} />
                        2. Agregar Ink IDs
                    </label>
                    
                    <ul className="nav nav-pills mb-3 bg-dark bg-opacity-50 p-1 rounded-pill" style={{ width: 'fit-content' }}>
                        <li className="nav-item">
                            <button 
                                className={`nav-link rounded-pill py-1 px-4 small ${activeTab === 'manual' ? 'active' : 'text-white'}`}
                                onClick={() => setActiveTab('manual')}
                            >
                                Manual
                            </button>
                        </li>
                        <li className="nav-item">
                            <button 
                                className={`nav-link rounded-pill py-1 px-4 small ${activeTab === 'search' ? 'active' : 'text-white'}`}
                                onClick={() => setActiveTab('search')}
                            >
                                Buscar
                            </button>
                        </li>
                    </ul>

                    <div className="tab-content animate__animated animate__fadeIn animate__faster">
                        {activeTab === 'manual' ? (
                            <div>
                                <textarea 
                                    className="form-control bg-dark text-white border-secondary py-2 focus-ring-primary"
                                    rows="4"
                                    placeholder="Ingresa Ink IDs separados por comas o saltos de línea..."
                                    value={manualInkIds}
                                    onChange={(e) => setManualInkIds(e.target.value)}
                                    style={{ resize: 'none' }}
                                />
                                <div className="form-text text-white-50 small mt-2">
                                    Ej: TST-12345,TST-67890,TST-55443
                                </div>
                            </div>
                        ) : (
                            <div className="bg-dark bg-opacity-50 rounded-3 p-3 border border-secondary border-opacity-10">
                                <div className="premium-search-group mb-3 shadow-sm">
                                    <span className="input-group-text">
                                        <FontAwesomeIcon icon={faSearch} />
                                    </span>
                                    <input 
                                        type="text"
                                        className="form-control ps-0 pe-2"
                                        placeholder="Buscar por Ink ID, Título, ISBN o Cuenta..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && fetchProjects(searchQuery)}
                                    />
                                    {searchQuery && (
                                        <button 
                                            className="btn-action px-3"
                                            onClick={() => { setSearchQuery(''); setProjects([]); }}
                                            title="Limpiar búsqueda"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                                    <button 
                                        className="btn-search"
                                        onClick={() => fetchProjects(searchQuery)}
                                        disabled={loadingProjects}
                                        title="Sincronizar"
                                    >
                                        {loadingProjects ? <LoadingIcon size="sm" /> : <FontAwesomeIcon icon={faSync} />}
                                    </button>
                                </div>

                                <div className="search-results overflow-auto pe-1" style={{ maxHeight: '250px' }}>
                                    {displayedProjects.map(project => {
                                        const fullId = `${project.account}-${project.ink_id}`;
                                        const isProjectSelected = selectedSearchIds.includes(fullId);
                                        return (
                                            <div 
                                                key={project.id || project.ink_id}
                                                className={`p-3 rounded-3 mb-2 d-flex justify-content-between align-items-center cursor-pointer border transition-all ${
                                                    isProjectSelected 
                                                        ? 'bg-primary bg-opacity-10 border-primary border-opacity-50' 
                                                        : 'bg-black bg-opacity-30 border-secondary border-opacity-10 hover-bg-black-50'
                                                }`}
                                                onClick={() => handleProjectToggle(fullId)}
                                            >
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="badge bg-dark text-primary border border-primary border-opacity-25">{fullId}</span>
                                                        <span className="fw-bold text-white small">{project.title}</span>
                                                    </div>
                                                    <div className="d-flex gap-2 small opacity-50">
                                                        <span className="text-info">{project.account}</span>
                                                        {project.isbn && <span className="text-white-50">• {project.isbn}</span>}
                                                    </div>
                                                </div>
                                                <div className={`rounded-circle d-flex align-items-center justify-content-center ${isProjectSelected ? 'bg-primary shadow-sm' : 'bg-secondary bg-opacity-20'}`} style={{ width: '24px', height: '24px' }}>
                                                    <FontAwesomeIcon 
                                                        icon={isProjectSelected ? faCheck : faPlus} 
                                                        className={`small ${isProjectSelected ? 'text-white' : 'text-white-50 opacity-10'}`}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {displayedProjects.length === 0 && !loadingProjects && (
                                        <div className="text-center py-5 text-white-50 small bg-black bg-opacity-20 rounded-3 border border-dashed border-secondary border-opacity-10 animate__animated animate__fadeIn">
                                            <FontAwesomeIcon icon={faSearch} size="2x" className="mb-3 opacity-10" />
                                            <p className="mb-0">
                                                {searchQuery ? `No se encontraron resultados para "${searchQuery}"` : "Ingresa un término para buscar proyectos"}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                {selectedSearchIds.length > 0 && (
                                    <div className="mt-3 pt-3 border-top border-secondary border-opacity-25">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="badge bg-primary rounded-pill">{selectedSearchIds.length}</span>
                                                <span className="small text-white-50">Ink IDs seleccionados</span>
                                            </div>
                                            <button className="btn btn-link btn-sm text-danger text-opacity-75 p-0 text-decoration-none small" onClick={() => setSelectedSearchIds([])}>Limpiar todo</button>
                                        </div>
                                        <div className="d-flex flex-wrap gap-2 pb-2 overflow-auto custom-scrollbar" style={{ maxHeight: '100px' }}>
                                            {selectedSearchIds.map(id => (
                                                <div key={id} className="bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded-pill px-3 py-1 d-flex align-items-center gap-2 animate__animated animate__fadeIn animate__faster">
                                                    <span className="small fw-bold text-primary">{id}</span>
                                                    <FontAwesomeIcon 
                                                        icon={faTimes} 
                                                        className="small text-white-50 cursor-pointer hover-text-danger transition-all" 
                                                        style={{ fontSize: '10px' }}
                                                        onClick={(e) => { e.stopPropagation(); handleProjectToggle(id); }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="mb-4">
                    <label className="fw-bold mb-3 d-flex align-items-center gap-2 text-white opacity-75 small text-uppercase">
                        <FontAwesomeIcon icon={faListCheck} />
                        3. Configuración
                    </label>
                    <div className="row g-3">
                        <div className="col-md-7">
                            <label className="small text-white-50 mb-1">Tipo de notificación</label>
                            <select 
                                className="form-select bg-dark text-white border-secondary py-2"
                                value={notificationType}
                                onChange={(e) => setNotificationType(e.target.value)}
                            >
                                <option value="3">Alta</option>
                                <option value="4">Actualización</option>
                                <option value="5">Baja</option>
                            </select>
                        </div>
                        <div className="col-md-5 d-flex align-items-end pb-1">
                            <div className="form-check form-switch bg-dark bg-opacity-50 p-2 px-4 rounded-pill border border-secondary border-opacity-10 w-100 d-flex align-items-center gap-2">
                                <input 
                                    className="form-check-input ms-0" 
                                    type="checkbox" 
                                    id="doneSwitch"
                                    checked={done === 1}
                                    onChange={(e) => setDone(e.target.checked ? 1 : 0)}
                                />
                                <label className="form-check-label text-white-50 small" htmlFor="doneSwitch">
                                    Terminado
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top border-secondary border-opacity-25">
                    <button className="btn btn-outline-secondary px-4 rounded-pill" onClick={onClose} disabled={submitting}>
                        Cancelar
                    </button>
                    <button 
                        className="btn btn-primary px-4 rounded-pill shadow-sm d-flex align-items-center gap-2" 
                        onClick={handleSubmit}
                        disabled={submitting || isInvalid}
                    >
                        {submitting ? <LoadingIcon size="sm" /> : <FontAwesomeIcon icon={faCheck} />}
                        Guardar Notificaciones
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default AddNotificationModal;
